import { Fragment, useCallback, useEffect, useState } from "react";
import { Row, Col, Spinner } from "react-bootstrap";
import BSForm from "react-bootstrap/Form";
import { IoIosArrowDown } from "react-icons/io";
import { BsPlusLg } from "react-icons/bs";
import styled from "styled-components";
import AnimateHeight from "react-animate-height";
import {
  CustomButton,
  CustomSelect
} from "components/CustomForm";
import PersianPickerTile from "components/PersianPickerTile";
import {
  getProductsReport,
  getProvinceExcelReport,
} from 'network/apis/ReportingApi';
import { getCorporations } from 'network/apis/CorporationApi';
import {
  getUnits,
  getIngredients,
  IN_STOCK,
  OUT_OF_STOCK,
} from "network/apis/CommonApi";
import { useMutation, useQuery } from "react-query";
import { useProvinceSelector, useProvinceDispatch } from 'province-app/redux/province.store';
import {
  handleDateRadioChange,
  pickFromDate,
  pickToDate,
  handleInputChange,
  handleCompanyChange,
  handleFilterTypeChange,
  goToPage,
  buildFiltersQuery,
  DateRadio,
  DefaultInputFilter,
  CompFilterType,
  FilterTypeOptions
} from "province-app/redux/filter.slice";
import Skeleton from "react-loading-skeleton";
import AddProductModal from "../AddProductModal";
import useModal from "hooks/useModal";
import { getProductStatistics } from "network/apis/ProductApi";
import useScreen from 'hooks/useScreen';
import { openLink } from 'helpers/general';

const Check = styled(BSForm.Check)`
  display: inline;
`;

const ResultBox = styled.div`
  border: 1px solid black;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1rem;
`;

const AddButton = styled.span`
  background-color: #784af6;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 1.4rem;
  cursor: pointer;
  position: absolute;
  bottom: 1.1rem;
`;

const FormLabel = styled.label`
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const ProductsSelect = styled(CustomSelect)`
    width: 50%;
`;

const ProductTabMenu = ({ tabData, onCreateSuccess }) => {
  const [currentData, setCurrentData] = useState();
  const { isOpen, handleOpenModal, handleCloseModal, data: editData } = useModal();

  const filterState = useProvinceSelector((store) => store.filter);
  const dispatch = useProvinceDispatch();

  const { size, lessThan1200, lessThan900, lessThan768 } = useScreen();

  useEffect(() => {
    setCurrentData(tabData);

    if (tabData) handleOpenModal(tabData);
  }, [tabData, handleOpenModal]);

  useEffect(() => {
    if (isOpen) setCurrentData(null);
  }, [isOpen]);

  const { data: statisticsData, isLoading: statisticsDataIsLoading } = useQuery(
    ["province-get-product-statistics", filterState.filterQuery],
    () => getProductStatistics(filterState.filterQuery),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: inputsData } = useQuery(
    ["get-ingredients"],
    () => getIngredients(),
    {
      refetchOnWindowFocus: false,
    }
  );
  const getInputOtions = () => {
    if (inputsData?.data) {
      const inputOptions = inputsData.data.map((input, index) => {
        return { value: input?.item_id, label: input?.name };
      });

      return [DefaultInputFilter, ...inputOptions];
    }

    return [];
  };

  const { data: corporationsData } = useQuery(["get-corporations"], () => getCorporations(), {
    refetchOnWindowFocus: false,
  });
  const getCompsOptions = () => {
    if (corporationsData?.data) {
      const compsOptions = corporationsData.data.map((corporation, index) => {
        return { value: corporation?.coop_id, label: corporation?.name };
      });

      return [CompFilterType, ...compsOptions];
    }

    return [];
  };

  const { refetch: fetchTableExcel } = useQuery(
    ["get-table-excel", filterState.productsFilterQuery],
    () => getProductsReport(filterState.productsFilterQuery + 'excel=true'),
    {
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: (data) => {
        const url = data?.list?.data?.url;
        if (url) openLink(url);
      },
    }
  );

  const { refetch: fetchProvinceExcel } = useQuery(
    ["get-province-excel", filterState.productsFilterQuery],
    () => getProvinceExcelReport('concentrate', '&' + filterState.productsFilterQuery),
    {
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: (data) => {
        const url = data?.data;
        if (url) openLink(url);
      },
    }
  );

  const downloadTableExcel = () => {
    fetchTableExcel();
  };

  const downloadProvinceExcel = () => {
    fetchProvinceExcel();
  };

  const isCompanyFilterSelected = () => (filterState.selectedFilterType?.value ?? -1) === 0;

  const getClassNameForFiltersRow = () => {
    if (lessThan900) {
      return 'mt-3 d-flex justify-content-between align-items-center flex-wrap-reverse';
    } else {
      const startPaddings = isCompanyFilterSelected() ? 'ms-1 ps-1' : 'ms-5 ps-5';
      return `${startPaddings} mt-3 d-flex justify-content-between align-items-center flex-wrap-reverse`;
    }
  };
  const dispatchFilters = () => {
		dispatch(goToPage(1));
		dispatch(buildFiltersQuery());
	};

  return (
    <Fragment>
      <Col
        md={lessThan768 ? 12 : 4}
        className={`d-flex align-items-center justify-content-${lessThan768 ? 'center mb-4' : 'end'}`}
      >
        <div className='ps-4 mx-5 d-flex flex-column align-items-center gap-2'>
          <CustomButton
            fontSize={10}
            onClick={downloadProvinceExcel}
            color='#0278EE'
            className='w-100'
          >
            اکسل شرکت ها
          </CustomButton>
          <CustomButton
            fontSize={10}
            onClick={downloadTableExcel}
            color='#0278EE'
            className='w-100'
          >
            اکسل جدول
          </CustomButton>
        </div>
        <ResultBox className="w-100">
          {statisticsDataIsLoading ? (
            <Skeleton count={1} width={34} height={20} />
          ) : (
            <span>{statisticsData?.data?.unit?.name}</span>
          )}
          {statisticsDataIsLoading ? (
            <Skeleton count={1} width={60} height={20} />
          ) : (
            <span>{`${statisticsData?.data?.sum ?? 0}`.persianCurrency()}</span>
          )}
          <span>:مجموع</span>
        </ResultBox>
      </Col>
      <Col md={lessThan768 ? 12 : 8}>
        <div
          className={`${lessThan900 ? 'ps-4' : 'ms-5 ps-5'} d-flex justify-content-between align-items-center`}
        >
          <div className="d-inline-flex flex-wrap align-items-center">
            <PersianPickerTile
              currentDate={filterState.pickedDateTo}
              topOffset={lessThan1200 ? '36%' : '18%'}
              leftOffset={lessThan1200 ? '3%' : '37%'}
              onPick={(pickedDate) => dispatch(pickToDate(pickedDate))}
            />
            <span className="mx-2">تا</span>
            <PersianPickerTile
              currentDate={filterState.pickedDateFrom}
              topOffset={lessThan1200 ? '36%' : '18%'}
              leftOffset={lessThan1200 ? '11%' : '49%'}
              onPick={(pickedDate) => dispatch(pickFromDate(pickedDate))}
            />
          </div>
          <div className="d-inline-flex flex-wrap justify-content-end">
            <FormLabel htmlFor={DateRadio.CustomDate}>بازه زمانی دلخواه</FormLabel>
            <Check
              className={`${lessThan1200 ? '' : 'me-3'} ms-2`}
              type="radio"
              id={DateRadio.CustomDate}
              checked={filterState.selectedRadio === DateRadio.CustomDate}
              onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
            />
            <FormLabel
              htmlFor={DateRadio.LastMonth}
              className={`${size.width < 560 ? 'mt-3' : ''}`}
            >
              تولید های یک ماه گذشته
            </FormLabel>
            <Check
              className={`ms-1 ${size.width < 560 ? 'mt-3' : ''}`}
              type="radio"
              id={DateRadio.LastMonth}
              checked={filterState.selectedRadio === DateRadio.LastMonth}
              onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
            />
          </div>
        </div>
        <div className={getClassNameForFiltersRow()}>
          <CustomButton
            className={`${lessThan900 ? 'w-100 my-3 ms-4' : ''}`}
            onClick={dispatchFilters}
          >
            نمایش
          </CustomButton>
          <div className='d-inline-flex align-items-center justify-content-end flex-grow-2'>
            {isCompanyFilterSelected() && (
              <CustomSelect
                placeholder='انتخاب شرکت'
                className={`ms-2 w-50`}
                radius="14"
                options={getCompsOptions()}
                onChange={(newCorporation) => dispatch(handleCompanyChange(newCorporation))}
                value={filterState.selectedCompany}
                menuPosition='fixed'
              />
            )}
            <ProductsSelect
              placeholder='همه شرکت ها'
              className="w-50 ms-3 me-3"
              radius="18"
              options={[...FilterTypeOptions]}
              onChange={(newFilter) => dispatch(handleFilterTypeChange(newFilter))}
              value={filterState.selectedFilterType}
              menuPosition='fixed'
            />
            <ProductsSelect
              className="w-50 me-4"
              radius="18"
              options={getInputOtions()}
              onChange={(newInput) => dispatch(handleInputChange(newInput))}
              value={filterState.selectedInput}
              menuPosition='fixed'
            />
            <FormLabel htmlFor={DateRadio.LastThreeMonths} className='text-end flex-shrink-0'>
              تولید های سه ماه گذشته
            </FormLabel>
            <Check
              className="ms-2"
              type="radio"
              id={DateRadio.LastThreeMonths}
              checked={filterState.selectedRadio === DateRadio.LastThreeMonths}
              onChange={(e) => dispatch(handleDateRadioChange(e.target.id))}
            />
          </div>
        </div>
      </Col>
      <AddButton onClick={() => handleOpenModal(null)} className={`${lessThan768 ? 'mt-4' : ''}`}>
        <div>
          <BsPlusLg size={12} />
        </div>
      </AddButton>
      {isOpen && <AddProductModal
        isOpen
        onClose={handleCloseModal}
        onCreateSuccess={onCreateSuccess}
        editData={editData}
      />}
    </Fragment>
  );
};

export default ProductTabMenu;
